import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PulseLoader } from "react-spinners";

import { Day1 } from "./modules/day-1/day-1";
import { Day2 } from "./modules/day-2/day-2";
import Day3 from "./modules/day-3/day-3";
import Day4 from "./modules/day-4/day-4";
import Day6 from "./modules/day-6/day-6";
import { GlobalDisplayComponent } from "./modules/global/globalDisplayComponent";
import Day7 from "./modules/day-7/day-7";
import { TvRuntimeProvider } from "../../utils/TvRuntimeContext";

function PlayerWallComponent() {
  const [TvId, setTvId] = useState(null);
  const [selectedDay, setSelectedDay] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);

  // last processed action timestamp (to compute delta reliably)
  const lastActionTsRef = useRef(0);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam) {
      setTvId(idParam);
    }
  }, [searchParams]);

  // sync selectedDay from the tv-scoped key
  useEffect(() => {
    if (!TvId) return;
    const dayKey = `tv${TvId}_selectedDay`;

    const initial = localStorage.getItem(dayKey);
    if (initial) setSelectedDay(initial);

    const onStorage = (e) => {
      if (e.key === dayKey && e.newValue) {
        setSelectedDay(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [TvId]);

  // helper: dispatch a batch of actions in ascending ts order, de-duped by ts
  const dispatchActionsBatch = (actions) => {
    if (!Array.isArray(actions) || actions.length === 0) return;
    // only new ones
    const fresh = actions
      .filter((a) => a?.ts && a.ts > lastActionTsRef.current)
      .sort((a, b) => a.ts - b.ts);

    for (const a of fresh) {
      const detail = { ...(a.payload || {}) }; // keep tvId!

      try {
        window.dispatchEvent(new CustomEvent(a.id, { detail }));
        lastActionTsRef.current = a.ts;
      } catch (err) {
        console.warn("Dispatch error for action:", a?.id, err);
      }
    }
  };

  // on mount and on any tv operator writes: compute delta and replay IN ORDER
  useEffect(() => {
    if (!TvId) return;

    const actionsKey = `tv${TvId}_activeActions`;

    // initial catch-up: replay the last 25 (ordered, delta-filtered)
    try {
      const initialArr = JSON.parse(localStorage.getItem(actionsKey) || "[]");
      const seed = initialArr.slice(-25);
      dispatchActionsBatch(seed);
    } catch (err) {
      console.warn("❗ Failed to parse initial actions:", err);
    }

    const onStorage = (e) => {
      if (e.key !== actionsKey) return;
      try {
        const arr = JSON.parse(e.newValue || "[]");
        dispatchActionsBatch(arr);
      } catch (err) {
        console.warn("❗ Failed to parse actions onStorage:", err);
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TvId]);

  // when the selected day changes, allow the newly-mounted day component to catch up
  useEffect(() => {
    if (!TvId || !selectedDay) return;

    const actionsKey = `tv${TvId}_activeActions`;
    try {
      // do NOT reset lastActionTsRef here; we only want to *replay* any actions
      // the component might have missed while mounting, not re-fire old ones.
      const actions = JSON.parse(localStorage.getItem(actionsKey) || "[]");
      // pick a reasonable window
      const recent = actions.slice(-25);
      dispatchActionsBatch(recent);
    } catch (err) {
      console.warn("❗ Failed to replay actions on day change:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TvId, selectedDay]);

  // loading animation
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(t);
  }, [selectedDay]);

  // exact same render logic as Home
  const renderDayComponent = () => {
    switch (selectedDay) {
      case "GLOBAL":
        return <GlobalDisplayComponent />;
      case "Day-1":
        return <Day1 />;
      case "Day-2":
        return <Day2 />;
      case "Day-3":
        return <Day3 />;
      case "Day-4":
        return <Day4 />;
      case "Day-6":
        return <Day6 />;
      case "Day-7":
        return <Day7 />;
      default:
        return <div className="text-muted">No component for {selectedDay}</div>;
    }
  };

  return (
    <TvRuntimeProvider tvId={TvId || null}>
      <motion.div
        className="flex flex-col items-center justify-center overflow-hidden min-h-screen bg-background text-foreground p-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="p-6 rounded-2xl w-full"
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <PulseLoader />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {renderDayComponent()}
            </AnimatePresence>
          )}
        </motion.div>
      </motion.div>
    </TvRuntimeProvider>
  );
}

export default PlayerWallComponent;
